import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {CashierPaymentWidget} from '../../components/CashierPaymentWidget';
import {ContinuePurchase} from '../../components/ContinuePurchase';
import {ErrorState} from '../../components/ErrorState/ErrorState';
import {ExternalLinksContainer} from '../../components/ExternalLinks/ExternalLinksContainer';
import {FreeDownload} from '../../components/FreeDownload';
import {GetEmailPage} from '../../components/GetEmailPage';
import {GetTitle} from '../../components/GetTitle/GetTitle';
import {GlobalLoader} from '../../components/GlobalLoader';
import {HideLoading} from '../../components/HideLoading';
import {NameYourPriceContainer} from '../../components/NameYourPrice';
import {PaymentErrorState} from '../../components/PaymentErrorState';
import {PaymentPending} from '../../components/PaymentPending';
import {PaymentProcessing} from '../../components/PaymentProcessing';
import {ThankYou} from '../../components/ThankYou/ThankYou';
import {Animation} from '../../components/ui/Animation';
import {Title} from '../../components/ui/Title';
import {FADE_IN_RIGHT, FADE_IN_UP} from '../../components/ui/TransitionsBehavior';
import {GET_CONTAINER_DISPLAY_M} from '../../machines/getContainerDisplay';
import {ENTITY_TYPES} from '../../services/albumProvider/albumProviderConstant';
import {entityTypeSelector} from '../../services/albumProvider/albumProviderSelector';
import {continuePaymentAction, retryPaymentAction} from '../../store/actions';
import {
  albumSelector,
  downloadLinksSelector,
  emailSelector,
  isCashierWidgetLoadingSelector,
  paymentErrorMessageSelector,
  snapshotIdSelector,
  trackSelector,
} from '../../store/selectors';
import {finalPriceSelector} from '../../store/selectors/price.selector';
import styles from './GetContainer.scss';
import {IOuter} from './GetContainer.types';

export const GetContainer: React.FunctionComponent<IOuter> = ({getContainerShow}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const album = useSelector(albumSelector);
  const track = useSelector(trackSelector);
  const entityType = useSelector(entityTypeSelector);
  const finalPrice = useSelector(finalPriceSelector);
  const downloadLinks = useSelector(downloadLinksSelector);
  const isCashierLoading = useSelector(isCashierWidgetLoadingSelector);
  const snapshotId = useSelector(snapshotIdSelector);
  const paymentErrorMessage = useSelector(paymentErrorMessageSelector);
  const email = useSelector(emailSelector);
  const currency = album.albumCurrency;
  const onPurchaseContinue = () => dispatch(continuePaymentAction());
  const handlePaymentErrorRetry = () => dispatch(retryPaymentAction());

  const getContentFn = () => {
    switch (getContainerShow) {
      case GET_CONTAINER_DISPLAY_M.SUBSCRIBE:
        return (
          <GetEmailPage
            subTitle={t('mgw.subscribeAndGet')}
            submitText={t('mgw.subscribeToDownload')}
            footer={t('mgw.subscribeWillSend')}
          />
        );
      case GET_CONTAINER_DISPLAY_M.NAME_YOUR_PRICE:
        return <NameYourPriceContainer />;
      case GET_CONTAINER_DISPLAY_M.FREE_DOWNLOAD_LINKS:
        if (!downloadLinks) {
          throw new Error('No downloadLinks');
        }
        return <FreeDownload freeDownloadLinks={downloadLinks} entityType={entityType} />;
      case GET_CONTAINER_DISPLAY_M.START_PURCHASE:
        return (
          <ContinuePurchase
            entityType={entityType}
            price={finalPrice}
            currency={currency}
            onBuyClick={onPurchaseContinue}
          />
        );

      case GET_CONTAINER_DISPLAY_M.NO_PURCHASE_AVAILABLE:
      default:
        return <div />;
    }
  };

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.PAYMENT_ERROR) {
    return <PaymentErrorState message={paymentErrorMessage} onRetryClick={handlePaymentErrorRetry} />;
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.ERROR) {
    return (
      <ErrorState>
        <Title className={styles.getContainerErrorTitle}>{t('mgw.errorStateTitle')}</Title>
      </ErrorState>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.ENTITY_NOT_GETTABLE) {
    const errorKey = {
      [ENTITY_TYPES.SINGLE]: 'mgw.singleErrorStateTitle',
      [ENTITY_TYPES.TRACK]: 'mgw.trackErrorStateTitle',
      [ENTITY_TYPES.ALBUM]: 'mgw.albumErrorStateTitle',
    }[entityType];
    return (
      <ErrorState>
        <Title className={styles.getContainerErrorTitle}>{t(errorKey)}</Title>
      </ErrorState>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.PROCESSING_PAYMENT) {
    return (
      <Animation motions={FADE_IN_RIGHT} animationKey={GET_CONTAINER_DISPLAY_M.PROCESSING_PAYMENT}>
        <PaymentProcessing />
      </Animation>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.PAYMENT_PENDING) {
    return (
      <Animation motions={FADE_IN_RIGHT} animationKey={GET_CONTAINER_DISPLAY_M.PAYMENT_PENDING}>
        <PaymentPending currency={currency} email={email} price={finalPrice} snapshotId={snapshotId} />
      </Animation>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.DOWNLOAD_LINKS) {
    return (
      <Animation motions={FADE_IN_RIGHT} animationKey={GET_CONTAINER_DISPLAY_M.DOWNLOAD_LINKS}>
        <ThankYou entityType={entityType} downloadLinks={downloadLinks} email={email} />
      </Animation>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.LOADING) {
    return <GlobalLoader />;
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.CASHIER_LAZY_WIDGET) {
    return (
      <HideLoading isLoading={isCashierLoading}>
        <CashierPaymentWidget />
      </HideLoading>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.SUBSCRIBE_STANDALONE) {
    return (
      <Animation motions={FADE_IN_RIGHT} animationKey={GET_CONTAINER_DISPLAY_M.SUBSCRIBE_STANDALONE}>
        <GetTitle id="getContainerTitle" entityType={entityType} className={styles.getContainerTitle} />
        <GetEmailPage subTitle={t('mgw.subscribeAndGetIOS')} submitText={t('mgw.subscribeContinueIOS')} />
      </Animation>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.GET_EMAIL_AND_PURCHASE) {
    return (
      <Animation motions={FADE_IN_RIGHT} animationKey={GET_CONTAINER_DISPLAY_M.GET_EMAIL_AND_PURCHASE}>
        <Title>{t('mgw.enterYourEmailTitle')}</Title>
        <GetEmailPage submitText={t('mgw.purchaseContinue')} />
      </Animation>
    );
  }

  if (getContainerShow === GET_CONTAINER_DISPLAY_M.DEMO_ALBUM) {
    return <Title className={styles.getContainerErrorTitle}>{t('mgw.demoAlbumErrorMessage')}</Title>;
  }

  return (
    <Animation
      className={styles.animationContainer}
      wrapperClassName={styles.animationContainer}
      motions={mainSectionMotions}
      animationKey={getContainerShow}
    >
      <section className={styles.mainContentWrapper} aria-labelledby="getContainerTitle">
        <GetTitle id="getContainerTitle" entityType={entityType} className={styles.getContainerTitle} />
        <div className={styles.getContainer}>
          <div className={styles.pageContent}>
            <div data-hook="get-content">{getContentFn()}</div>
            <ExternalLinksContainer album={album} track={track} />
          </div>
        </div>
      </section>
    </Animation>
  );
};

GetContainer.displayName = 'GetContainer';

const mainSectionMotions = FADE_IN_UP;
